'use client'

import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { findBrandBySlug, useGetCityBrandsQuery } from '@/api/brands/get-brand-query'
import { useGetRestaurantsQuery } from '@/api/restaurants/get-restaurants-query'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { Locale } from '@/setup/locales'
import { getLinkHref } from '@/setup/routing'

const MAX_SIBLING_LINKS = 6

export const RestaurantDetailBrandLinks = () => {
    const { restaurant, citySlug } = useRestaurantDetailContext()
    const t = useTranslations()
    const locale = useLocale() as Locale

    const brandSlug = restaurant?.brandSlug
    const resolvedCitySlug = restaurant?.city?.slug ?? citySlug

    const { data: brandsData } = useGetCityBrandsQuery(resolvedCitySlug ?? '')
    const { data } = useGetRestaurantsQuery({
        citySlug: resolvedCitySlug,
        brandSlug,
    })

    const brand = findBrandBySlug(brandsData?.data, brandSlug)
    const cityName = restaurant?.city?.name

    if (!restaurant || !brandSlug || !brand || !resolvedCitySlug || !cityName) return null

    const siblings = (data?.data ?? []).filter((sibling) => sibling.slug !== restaurant.slug)

    if (!siblings.length) return null

    const brandHref = getLinkHref(
        {
            pattern: '/restaurants/[citySlug]/brands/[brandSlug]',
            params: { citySlug: resolvedCitySlug, brandSlug },
        },
        locale
    )

    return (
        <section
            className="flex flex-col w-full pb-10 md:pb-14 mb-10 md:mb-14 border-b-[1px]
                border-b-gray-200"
        >
            <h2 className="text-[20px] md:text-[24px] font-semibold mb-5 md:px-0 px-1">
                {t('restaurants.detail.brandLocations.title', {
                    brandName: brand.name,
                    cityName,
                })}
            </h2>

            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 md:px-0 px-1">
                {siblings.slice(0, MAX_SIBLING_LINKS).map((sibling) => (
                    <li
                        key={sibling.slug}
                        className="border-b-[1px] border-b-gray-100 last:border-b-0"
                    >
                        <Link
                            href={getLinkHref(
                                {
                                    pattern: '/restaurants/[citySlug]/[restaurantSlug]',
                                    params: {
                                        citySlug: resolvedCitySlug,
                                        restaurantSlug: sibling.slug,
                                    },
                                },
                                locale
                            )}
                            className="flex flex-col py-3 hover:opacity-70 transition-opacity"
                        >
                            <span className="text-[15px] md:text-[16px] font-medium">
                                {sibling.name}
                            </span>

                            {sibling.neighborhood && (
                                <span className="text-[13px] md:text-[14px] text-black/60">
                                    {sibling.neighborhood}
                                </span>
                            )}
                        </Link>
                    </li>
                ))}
            </ul>

            <Link
                href={brandHref}
                className="mt-4 md:px-0 px-1 text-[15px] md:text-[16px] font-medium underline w-fit
                    hover:opacity-70 transition-opacity"
            >
                {t('restaurants.detail.brandLocations.showAll', {
                    brandName: brand.name,
                    cityName,
                })}
            </Link>
        </section>
    )
}
